import { create } from 'zustand';

/**
 * Notification panel UI state.
 *
 * - `open`: panel visibility (controls the `<Sheet>` `open` prop)
 * - `seenDuringOpen`: ids of unread rows that became visible while the
 *   panel was open. On close, these are sent to MarkNotificationAsRead.
 *   Mirrors Flutter `bloc.notificationIds` (set on visibility >80% per
 *   `notification_center_screen.dart:140-141`), drained on dispose
 *   (`notification_center_screen.dart:51-53`).
 *
 * **Why Zustand and not React state?** The panel mount lives in
 * `App.tsx`; the open trigger lives in `side-nav.tsx`. A shared store
 * lets the Bell click toggle from the nav without prop-drilling through
 * the layout shell.
 */
interface NotificationPanelState {
  open: boolean;
  seenDuringOpen: Set<string>;
  openPanel: () => void;
  closePanel: () => void;
  markSeenWhileOpen: (id: string) => void;
  resetSeen: () => void;
}

export const useNotificationPanelStore = create<NotificationPanelState>((set) => ({
  open: false,
  seenDuringOpen: new Set<string>(),
  openPanel: () => set({ open: true, seenDuringOpen: new Set<string>() }),
  closePanel: () => set({ open: false }),
  markSeenWhileOpen: (id) =>
    set((state) => {
      if (state.seenDuringOpen.has(id)) return state;
      const next = new Set(state.seenDuringOpen);
      next.add(id);
      return { seenDuringOpen: next };
    }),
  resetSeen: () => set({ seenDuringOpen: new Set<string>() }),
}));
